/* swt-quick-view.js · Swiftronics compact quick-add modal
   Replaces Dawn's quick-add-modal (which fetched and rendered the ENTIRE
   product page inside a popup) with a lightweight, self-contained quick view
   built from data already rendered in the card:
     - variant option pills (no page fetch)
     - live price / compare-at / availability / variant image
     - quantity stepper
     - add to cart via AJAX; opens the Rebuy Smart Cart when present and
       falls back to the /cart page (or a native submit) when it is not,
       so the modal works with or without Rebuy installed.
   Markup lives in snippets/card-product.liquid inside a <template> per card;
   the template is cloned into a body-level overlay on open and destroyed on
   close (no state leakage, and position:fixed is never trapped by the card's
   hover transform). */
(function () {
  if (customElements.get('swt-quick-view')) return;

  var lockScroll = function (lock) {
    document.documentElement.classList.toggle('swt-qv-lock', lock);
  };

  class SwtQuickView extends HTMLElement {
    connectedCallback() {
      if (this.swtBound) return;
      this.swtBound = true;
      this.template = this.querySelector('template');
      var trigger = this.querySelector('[data-swt-qv-open]');
      if (!trigger || !this.template) return;
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    }

    disconnectedCallback() {
      this.destroy();
    }

    open() {
      this.destroy();

      var host = document.createElement('div');
      host.className = 'swt-qv';
      host.innerHTML = '<div class="swt-qv__backdrop" data-swt-qv-close></div>';

      var panel = document.createElement('div');
      panel.className = 'swt-qv__panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.appendChild(this.template.content.cloneNode(true));
      host.appendChild(panel);
      document.body.appendChild(host);
      this.host = host;

      lockScroll(true);
      this.wire(panel);

      this.keyHandler = (event) => {
        if (event.key === 'Escape') this.destroy();
      };
      document.addEventListener('keydown', this.keyHandler);
      host.addEventListener('click', (event) => {
        if (event.target.closest('[data-swt-qv-close]')) this.destroy();
      });

      // Close automatically once the item lands in the cart (the cart drawer
      // or notification takes over from there).
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          window.setTimeout(() => this.destroy(), 120);
        });
      }

      window.requestAnimationFrame(function () {
        host.classList.add('is-open');
      });

      var focusable = panel.querySelector('.swt-qv__pill.is-active') || panel.querySelector('button, [href], input');
      if (focusable) focusable.focus({ preventScroll: true });
    }

    destroy() {
      if (this.keyHandler) {
        document.removeEventListener('keydown', this.keyHandler);
        this.keyHandler = null;
      }
      if (this.unsubscribeCart) {
        this.unsubscribeCart();
        this.unsubscribeCart = null;
      }
      if (this.host) {
        this.host.remove();
        this.host = null;
      }
      lockScroll(false);
    }

    wire(panel) {
      var self = this;
      var dataEl = panel.querySelector('[data-swt-qv-json]');
      var variants = [];
      try {
        variants = dataEl ? JSON.parse(dataEl.textContent) : [];
      } catch (error) {
        variants = [];
      }

      var idInput = panel.querySelector('input[name="id"]');
      var priceEl = panel.querySelector('[data-swt-qv-price]');
      var compareEl = panel.querySelector('[data-swt-qv-compare]');
      var imageEl = panel.querySelector('[data-swt-qv-image]');
      var submitButton = panel.querySelector('[data-swt-qv-submit]');
      var submitLabel = submitButton ? submitButton.querySelector('span') : null;
      var defaultImage = imageEl ? imageEl.getAttribute('src') : null;

      var strings = window.variantStrings || {};
      var textAdd = strings.addToCart || 'Add to cart';
      var textSoldOut = strings.soldOut || 'Sold out';
      var textUnavailable = strings.unavailable || 'Unavailable';

      var groups = Array.prototype.slice.call(panel.querySelectorAll('[data-swt-qv-group]'));
      var selected = groups.map(function (group) {
        var active = group.querySelector('.swt-qv__pill.is-active');
        return active ? active.getAttribute('data-value') : null;
      });

      var findVariant = function (options) {
        return variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return option === options[index];
          });
        });
      };

      var update = function () {
        var variant = findVariant(selected);

        groups.forEach(function (group, groupIndex) {
          group.querySelectorAll('.swt-qv__pill').forEach(function (pill) {
            var test = selected.slice();
            test[groupIndex] = pill.getAttribute('data-value');
            var match = findVariant(test);
            pill.classList.toggle('is-dim', !match || !match.available);
          });
        });

        if (!variant) {
          if (idInput) idInput.disabled = true;
          if (submitButton) submitButton.disabled = true;
          if (submitLabel) submitLabel.textContent = textUnavailable;
          return;
        }

        if (idInput) {
          idInput.disabled = false;
          idInput.value = variant.id;
        }
        if (priceEl) priceEl.textContent = variant.price;
        if (compareEl) {
          compareEl.textContent = variant.compare || '';
          compareEl.hidden = !variant.compare;
        }
        if (imageEl && (variant.image || defaultImage)) {
          imageEl.setAttribute('src', variant.image || defaultImage);
        }
        if (submitButton) submitButton.disabled = !variant.available;
        if (submitLabel) submitLabel.textContent = variant.available ? textAdd : textSoldOut;
      };

      groups.forEach(function (group, index) {
        group.addEventListener('click', function (event) {
          var pill = event.target.closest('.swt-qv__pill');
          if (!pill) return;
          event.preventDefault();
          group.querySelectorAll('.swt-qv__pill').forEach(function (button) {
            button.classList.remove('is-active');
          });
          pill.classList.add('is-active');
          selected[index] = pill.getAttribute('data-value');
          update();
        });
      });

      var qtyInput = panel.querySelector('input[name="quantity"]');
      panel.querySelectorAll('[data-swt-qv-qty]').forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          if (!qtyInput) return;
          var step = parseInt(qtyInput.getAttribute('step'), 10) || 1;
          var min = parseInt(qtyInput.getAttribute('min'), 10) || 1;
          var value = parseInt(qtyInput.value, 10) || min;
          value += button.getAttribute('data-swt-qv-qty') === '+' ? step : -step;
          qtyInput.value = Math.max(min, value);
        });
      });

      /* Add-to-cart: capture-phase listener on the panel fires BEFORE Dawn's
         <product-form> submit handler on the form, so we fully own the flow.
         Dawn's handler would redirect to /cart because this theme has no
         cart-drawer / cart-notification element. */
      var form = panel.querySelector('form[action*="/cart/add"]') || panel.querySelector('form');
      if (form) {
        panel.addEventListener('submit', function (event) {
          if (event.target !== form) return;
          event.preventDefault();
          event.stopPropagation();
          if (submitButton) submitButton.disabled = true;
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: new FormData(form)
          })
            .then(function (response) {
              if (!response.ok) throw new Error('atc failed');
              return response.json();
            })
            .then(function () {
              document.dispatchEvent(new CustomEvent('swt:cart:added'));
              self.destroy();
              if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
                window.Rebuy.SmartCart.show();
              } else {
                window.location.href = '/cart';
              }
            })
            .catch(function () {
              form.submit();
            })
            .finally(function () {
              if (submitButton) submitButton.disabled = false;
            });
        }, true);
      }

      update();
    }
  }

  customElements.define('swt-quick-view', SwtQuickView);
})();